import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Target, X, Trash2 } from "lucide-react";
import Button from "../UI/Button";
import { UI } from "../UI/uiTokens";

/* ==========================================================
AddMilestoneModal (WITH TEMPLATE SUPPORT)
========================================================== */
const Milestonemodal = ({ onClose, onSave, children: childrenList = [] }) => {
  const [childId, setChildId] = useState("");
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [notes, setNotes] = useState("");
  const [objectives, setObjectives] = useState([""]);

  // ✅ TEMPLATE STATES
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const filteredTemplates = templates.filter(
  (t) => t.category === category
);

  // optional: alphabetical children
  const sortedChildren = useMemo(() => {
    return [...childrenList].sort((a, b) => {
      const an = (a.fullName || a.name || "").toLowerCase();
      const bn = (b.fullName || b.name || "").toLowerCase();
      return an.localeCompare(bn);
    });
  }, [childrenList]);

  const resetForm = () => {
    setChildId("");
    setCategory("");
    setTitle("");
    setDescription("");
    setTargetDate("");
    setNotes("");
    setObjectives([""]);
    setSelectedTemplate("");
  };

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line
  }, []);

  /* ===============================
     FETCH TEMPLATES
  =============================== */
useEffect(() => {
  const fetchTemplates = async () => {
    try {
      const API_BASE =
        process.env.REACT_APP_API_BASE || "";

      const res = await fetch(`${API_BASE}/api/templates`);
      const data = await res.json();

      console.log("TEMPLATES:", data);
      setTemplates(data.templates || []);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  fetchTemplates();
}, []);

  /* ===============================
     OBJECTIVES HANDLING
  =============================== */
  const addObjective = () => setObjectives((prev) => [...prev, ""]);

  const updateObjective = (value, index) => {
    setObjectives((prev) => prev.map((o, i) => (i === index ? value : o)));
  };

  const removeObjective = (index) => {
    setObjectives((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [""];
    });
  };

  const closeAndReset = () => {
    resetForm();
    onClose?.();
  };

  /* ===============================
     SUBMIT
  =============================== */
  const submitForm = () => {
    if (!childId || !category || !title.trim() || !targetDate) {
      toast.error("Please fill required fields (Child, Category, Title, Target Date).");
      return;
    }

    const cleanedObjectives = (objectives || [])
      .map((x) => String(x || "").trim())
      .filter(Boolean);

    onSave?.({
      childId: Number(childId),
      category,
      title: title.trim(),
      description: String(description || "").trim(),
      targetDate,
      objectives: cleanedObjectives,
      notes: String(notes || "").trim(),
    });

    resetForm();
  };

  /* ===============================
     TEMPLATE APPLY
  =============================== */
  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);

    const template = templates.find((t) => String(t.id) === templateId);

    if (template) {
      setTitle(template.title || "");
      setDescription(template.description || "");
      setCategory(template.category || "");
      setObjectives(template.objectives || [""]);
    }
  };

  return (
    <div className={UI.modalOverlay}>
    <div className={`${UI.modalBox} w-full max-w-5xl`}>
        {/* HEADER */}
        <div className={UI.modalHeader}>
          <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Target className="h-5 w-5 text-purple-600" />
            Add development records
          </h2>

          <button
            onClick={closeAndReset}
            className={`${UI.iconBtn} text-gray-700 dark:text-gray-200`}
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* BODY */}
        <div className={`${UI.modalBody} space-y-4 max-h-[75vh] overflow-y-auto`}>
          
          {/* CHILD + CATEGORY */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Child</label>
              <select
                value={childId}
                onChange={(e) => setChildId(e.target.value)}
                className={UI.select}
              >
                <option value="">Select child</option>
                {sortedChildren.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.fullName || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
              <select
                value={category}
                onChange={(e) => {
                const selectedCategory = e.target.value;
                setCategory(selectedCategory);

                // filter templates based on category
                const matched = templates.filter(
                  (t) => t.category === selectedCategory
                );

                if (matched.length > 0) {
                  const template = matched[0]; // auto pick first

                  setSelectedTemplate(String(template.id));
                  setTitle(template.title || "");
                  setDescription(template.description || "");
                  setObjectives(template.objectives || [""]);
                } else {
                  // reset if no template
                  setSelectedTemplate("");
                  setTitle("");
                  setDescription("");
                  setObjectives([""]);
                }
              }}
                className={UI.select}
              >
                <option value="">Select category</option>
                <option value="Physical">Physical</option>
                <option value="Educational">Educational</option>
                <option value="Social">Social</option>
                <option value="Emotional">Emotional</option>
              </select>
            </div>
          </div>

          {/* ✅ TEMPLATE DROPDOWN */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Template</label>
            <select
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className={UI.select}
            >
              <option value="">Select template (optional)</option>
              {filteredTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>

          {/* TITLE */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Milestone Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={UI.input}
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={UI.textarea}
            />
          </div>

          {/* TARGET DATE */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Target Date *</label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className={UI.input}
            />
          </div>

          {/* OBJECTIVES */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Objectives</label>

            {objectives.map((obj, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  value={obj}
                  onChange={(e) => updateObjective(e.target.value, i)}
                  className={UI.input}
                />

                {objectives.length > 1 && (
                  <button onClick={() => removeObjective(i)} type="button">
                    <Trash2 />
                  </button>
                )}
              </div>
            ))}

            <button onClick={addObjective} className={UI.btnOutline} type="button">
              <Plus /> Add Objective
            </button>
          </div>

          {/* NOTES */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={UI.textarea}
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className={UI.modalFooter}>
          <Button onClick={closeAndReset} variant="outline">
            Cancel
          </Button>
          <Button onClick={submitForm} variant="primary">
            Create Records
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Milestonemodal;